import { DeliveryBarMachineContext } from '@monofront/business/contexts';
import Label from '@monofront/design-system/components/Form/Label';
import { useState } from 'react';
import { SlotsTable } from '@features/DeliveryBar/Panels/ShippingMethodSelectionPanel/ShippingMethodSelector/SlotsTable/SlotsTable';
import { useTranslations } from 'next-intl';
import { ShippingMethodTypes } from '@monofront/types';
import UltraFresh from '@monofront/design-system/visuals/Pictograms/UltraFresh';
import { trackEvent } from '@monofront/tracking';
import { DeliveryBarMachineContextShippingMethod } from '@monofront/business/stateMachines/deliveryBar/types';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar/events';

enum SupportedTheme {
  BLUE = 'blue',
  GREEN = 'green',
  RED = 'red',
}

type Label = {
  color: SupportedTheme;
  text: string;
};

type ShippingMethodSelectorProps = {
  shippingMethodType: ShippingMethodTypes;
  carrierId: string;
  title: string;
  subtitle: string;
  description: string;
  labels: Label[] | undefined;
  eligibleShippingMethod: DeliveryBarMachineContextShippingMethod;
  mode?: 'selection' | 'information';
};

const getTheme = (theme: SupportedTheme) => {
  const baseTheme = 'tw-lf-label ';

  const config = {
    [SupportedTheme.BLUE]: baseTheme + 'tw-lf-label-decorative-blue',
    [SupportedTheme.GREEN]: baseTheme + 'tw-lf-label-decorative-green',
    [SupportedTheme.RED]: baseTheme + 'tw-lf-label-decorative-red',
  };

  return config[theme];
};

export const ShippingMethodSelector: React.FC<ShippingMethodSelectorProps> = ({
  title,
  subtitle,
  description,
  labels,
  shippingMethodType,
  carrierId,
  eligibleShippingMethod,
  mode = 'selection',
}) => {
  const isSelectionMode = mode === 'selection';
  const [isShowMoreDisplayed, setIsShowMoreDisplayed] = useState(false);
  const hasThresholds = eligibleShippingMethod?.configurations?.some(
    (configuration) => configuration.thresholds?.length,
  );

  const tShippingMethodSelection = useTranslations(
    'DeliveryBar.ShippingMethodSelection',
  );
  const { send } = DeliveryBarMachineContext.useActorRef();

  return (
    <>
      <div
        data-testId={`Deliverybar-shipping-method-${shippingMethodType}`}
        className="tw-mb-3 tw-flex tw-items-center tw-justify-between"
      >
        <div className="tw-flex tw-flex-col tw-gap-2">
          {title && <h3 className="tw-lf-title-xs">{title}</h3>}
          {subtitle && (
            <p className="tw-lf-body-bold-sm tw-text-blue-600">{subtitle}</p>
          )}
        </div>
        {isSelectionMode && (
          <button
            className="tw-lf-button tw-lf-button-primary tw-lf-body-bold-sm"
            type="button"
            data-testid={`Deliverybar-choose-button-${shippingMethodType}`}
            onClick={() => {
              send({
                type: EVENTS.SHIPPING_METHOD_SELECT,
                shippingMethod: {
                  type: shippingMethodType,
                  carrierId,
                },
              });

              trackEvent({
                type: 'LFPickDeliveryOption',
                data: { shipping_method: shippingMethodType },
              });
            }}
          >
            {tShippingMethodSelection('choose')}
          </button>
        )}
      </div>
      {!!labels?.length && (
        <p className="tw-mb-4 tw-flex tw-gap-2">
          {labels.map(({ text, color }, index) => {
            const className = getTheme(color);

            return (
              <span key={index} className={className}>
                {text}
              </span>
            );
          })}
        </p>
      )}
      {description && (
        <p className="tw-lf-body-regular-sm tw-mb-4">{description}</p>
      )}

      {hasThresholds ? (
        <>
          {isSelectionMode && (
            <button
              className="tw-lf-button-clear tw-lf-body-bold-sm tw-underline tw-underline-offset-2"
              onClick={() => setIsShowMoreDisplayed((state) => !state)}
              data-testid={`Deliverybar-show-more-button-${shippingMethodType}`}
            >
              {tShippingMethodSelection(
                isShowMoreDisplayed ? 'hidePrices' : 'showPrices',
              )}
            </button>
          )}
          {(isShowMoreDisplayed || !isSelectionMode) && (
            <>
              <SlotsTable
                shippingMethod={eligibleShippingMethod}
                UNSAFE_className="tw-mt-2"
              />
              {shippingMethodType === ShippingMethodTypes.COURIER && (
                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1">
                  <UltraFresh UNSAFE_className="tw-min-h-4 tw-min-w-4" />
                  <span className="tw-lf-body-regular-sm tw-text-[12px] tw-leading-[150%]">
                    {tShippingMethodSelection('UFExplanationBlock')}
                  </span>
                </div>
              )}
            </>
          )}
        </>
      ) : null}
    </>
  );
};
